import { useState } from 'react';
import { MessageCircle } from 'lucide-react';

import { DebriefView } from './DebriefView.tsx';
import { EmptyState } from './EmptyState';
import { t } from '../i18n/t';
import { describeSession, type PastSession } from './sessions.ts';

/**
 * The learner's past conversations, and optionally the debrief for one.
 *
 * Was `HistoryView`'s `PastConversations`, itself ported from the deleted
 * `ParentArea`. Extracted because two screens now render it: the progress
 * screen in the adult register, and `/account` in the kid register.
 *
 * `showDebrief` is the spec §5 decision, as a prop. A debrief is an
 * analytical breakdown of the learner's errors and is written for a parent
 * (§10); the parent gate that used to stand in front of it is gone, so the
 * register decides instead -- and it decides by not rendering the control at
 * all, rather than by hiding what it rendered.
 */
export function SessionList({ sessions, showDebrief }: { sessions: readonly PastSession[]; showDebrief: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  // Read once per render rather than per row: a list straddling midnight
  // would otherwise label its first rows against a different day from its
  // last.
  const now = new Date();
  const rows = sessions.map((s) => describeSession(s, now));

  if (rows.length === 0) {
    return <EmptyState icon={MessageCircle} body={t('session-none')} />;
  }

  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => {
        const canOpen = showDebrief && row.readable;
        return (
          <li key={row.id} className="border-base-300 rounded-box border">
            {/* The padding is on the button, not the `li`, so the whole card is
                the target: it used to end at the text, leaving a ring inside
                the border that looked clickable and was not. Nothing here is a
                daisyUI `btn`, so the pointer and the hover tint have to be
                stated -- `enabled:` withholds both from a row that cannot
                open. */}
            <button
              type="button"
              className="rounded-box enabled:hover:bg-base-200 flex w-full items-baseline justify-between gap-3 p-3 text-start transition-colors enabled:cursor-pointer"
              disabled={!canOpen}
              // A disclosure, so the state has to be announced -- but only
              // where there is one: a row that cannot open is not a collapsed
              // disclosure, it is a plain line of text.
              aria-expanded={canOpen ? open === row.id : undefined}
              onClick={() => setOpen((o) => (o === row.id ? null : row.id))}
            >
              <span>{row.what}</span>
              <span className="text-base-content/60 shrink-0 text-xs">
                {row.readable ? row.when : t('session-in-progress')}
              </span>
            </button>
            {showDebrief && open === row.id && (
              // The padding the `li` used to carry, now that the button has it:
              // the waiting and failed states are bare paragraphs and would
              // otherwise sit against the border.
              <div className="px-3 pb-3">
                {/* `ended` is unconditionally true: every row that can be
                    opened belongs to a session that is no longer running,
                    which is what `readable` means. */}
                <DebriefView sessionId={row.id} ended />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
